import jsPDF from "jspdf";
import { loadYoga, Node, Yoga } from "yoga-layout/load";
import { applyStyle, FlexStyle } from "./FlexStyle";

export let yoga: Yoga | undefined;
export async function initYoga() {
    yoga = await loadYoga();
}

export function getYoga() {
    return yoga!
}

export const MM_PER_POINT = 0.352777778;
export const A4MM = {
    width: 210,
    height: 297
}

export const A3MM = {
    height: 420,
    width: 297
}

export class BasePDFNode {
    constructor(public pdf: jsPDF, public style: FlexStyle = {} , layout: Node | undefined = undefined, public parent: BasePDFNode | undefined = undefined) {
        if (layout == undefined) {
            this.layout = getYoga().Node.create();
        } else {
            this.layout = layout;
        }
    }

    public children: BasePDFNode[] = [];
    public layout: Node;

    public addChild(NodeClass: typeof BasePDFNode, style: FlexStyle = {}) {
        let c = new NodeClass(this.pdf, style, getYoga().Node.create(), this)
        this.children.push(c);
        return c;
    }

    public addChild2<T extends BasePDFNode>(c: T): T {
        c.parent = this;
        this.children.push(c);
        return c;
    }

    public init() {
        for (let c of this.children) {
            c.init();
        }
    }

    public applyStyles() {
        applyStyle(this.layout, this.style);
        for (let i = 0; i < this.children.length; i++) {
            let c = this.children[i];
            c.applyStyles();
            this.layout.insertChild(c.layout, i);
        }
    }

    public getTopLeft(): { x: number, y: number } {
        let ret = {
            x: 0,
            y: 0
        }
        if (this.parent != undefined) {
            ret = this.parent.getTopLeft();
        }
        return {
            x: ret.x + this.layout.getComputedLeft(),
            y: ret.y + this.layout.getComputedTop()
        }
    }

    public render() {
        for (let c of this.children) {
            c.render();
        }
    }

}

export type RectStyle = {
    borderColor?: string,
    fillColor?: string
} & FlexStyle;

export class RectPDFNode extends BasePDFNode {
    constructor(public pdf: jsPDF, style: Partial<RectStyle> = {}, layout: Node | undefined = undefined, public parent: BasePDFNode | undefined = undefined) {
        super(pdf, style, layout, parent);
        this.rectStyle = Object.assign({
            borderColor: '#000000'
        }, style);;
    }

    public render(): void {
        let l = this.layout.getComputedLayout();
        let topLeft = this.getTopLeft();

        let style = "S";
        let draw = false;
        if (this.rectStyle.fillColor != undefined) {
            style = "FD";
            this.pdf.setFillColor(this.rectStyle.fillColor);
            draw = true;
        }
        if (this.rectStyle.borderColor != undefined) {
            this.pdf.setDrawColor(this.rectStyle.borderColor);
            draw = true;
        }
        if (draw) {
            this.pdf.rect(topLeft.x, topLeft.y, l.width, l.height, style);
        }
        super.render();
    }

    public rectStyle: RectStyle;
}

export type TextStyle = {
    fontSize: number,
    lineHeight: number,
    vertical: boolean,
    textColor?: string,
    textWeight: "normal" | "bold" | "italic" | "bolditalic",
    align?: "left" | "right"
} & RectStyle;

export class TextPDFNode extends RectPDFNode {

    public text: string = "";

    constructor(public pdf: jsPDF, style: Partial<TextStyle> = {}, layout: Node | undefined = undefined, public parent: BasePDFNode | undefined = undefined) {
        super(pdf, style, layout, parent);
        this.textStyle = Object.assign({
            fontSize: 8,
            lineHeight: 1,
            vertical: false,
            textWeight: "normal"
        }, style);
        if (style.borderColor == undefined) {
            this.rectStyle.borderColor = undefined;
        }
    }

    public textStyle: TextStyle;

    public applyStyles(): void {
        if (this.textStyle.width != undefined && this.textStyle.width != undefined) {
            let w = typeof this.textStyle.width === 'number' ? this.textStyle.width : undefined;
            if (w != undefined) {
                let lines = this.pdf
                    .setFont("helvetica", this.textStyle.textWeight)
                    .setFontSize(this.textStyle.fontSize)
                    .setLineHeightFactor(this.textStyle.lineHeight)
                    .splitTextToSize(this.text, w);
                let textHeight = ((lines.length) * this.textStyle.fontSize * this.textStyle.lineHeight * MM_PER_POINT);
                this.style.height = textHeight;
            }


        } else {
            // let textHeight = this.textStyle.fontSize * this.textStyle.lineHeight * MM_PER_POINT;
            // let width = this.pdf
            //         .setFont("helvetica")
            //         .setFontSize(this.textStyle.fontSize)
            //         .setLineHeightFactor(this.textStyle.lineHeight)
            //     .getStringUnitWidth(this.text) * this.textStyle.fontSize * MM_PER_POINT;
            // this.style.height = textHeight;
            // this.style.width = width + 1;
            // if (this.text == "DO") {
            //     console.log(`DO Width: ${width} | FontSize: ${this.textStyle.fontSize}`);
            // }

            this.layout.setMeasureFunc((width, widthMode, height, heightMode) => {
                let lines = this.pdf
                    .setFont("helvetica", this.textStyle.textWeight)
                    .setFontSize(this.textStyle.fontSize)
                    .setLineHeightFactor(this.textStyle.lineHeight)
                    .splitTextToSize(this.text, width);
                let textHeight = ((lines.length) * this.textStyle.fontSize * this.textStyle.lineHeight * MM_PER_POINT);

                let ret = {
                    height: Math.min(textHeight, height), //textHeight,
                    width: Math.min(this.pdf.getStringUnitWidth(lines[0]) * this.textStyle.fontSize * MM_PER_POINT, width)
                }
                this.measuredH = textHeight;
                // console.log(`Text measure ${this.text} ${textHeight}, ${height}, ${heightMode}`);
                return ret;
            });

        }
        super.applyStyles();
    }
    public measuredH = 0;

    public render(): void {
        super.render();
        let l = this.layout.getComputedLayout();
        // console.log(`${this.text} Computed ${l.height}, ${this.measuredH}`);
        let topLeft = this.getTopLeft();
        // console.log("Computed", l);
        let lines = this.pdf
            .setFont("helvetica", this.textStyle.textWeight)
            .setFontSize(this.textStyle.fontSize)
            .setLineHeightFactor(this.textStyle.lineHeight)
            .splitTextToSize(this.text, l.width);
        if (this.textStyle.textColor != undefined) {
            this.pdf.setTextColor(this.textStyle.textColor);
        } else {
            this.pdf.setTextColor("#000000");
        }
        if (this.textStyle.align == "right") {
            this.pdf.text(lines, topLeft.x + l.width, topLeft.y, {
                align: "right",
                baseline: "top",
                lineHeightFactor: 1,
                angle: this.textStyle.vertical ? -90 : 0
            });    
        } else {
            this.pdf.text(lines, topLeft.x, topLeft.y, {
                baseline: "top",
                lineHeightFactor: 1,
                angle: this.textStyle.vertical ? -90 : 0
            });
    
        }

    }
}
